<template>
  <div class="nav-margin">
    <navigation></navigation>
    <nonPremiumJobScreen v-if="!isPremiumAccount" />
    <div v-if="isPremiumAccount">
      <div
        class="action-bar bg-white border-bottom border-gray-300 d-flex gap-12 justify-content-between flex-wrap gap-2 py-10 px-4 sticky-top">
        <div class="d-flex align-items-center gap-12">
          <div class="position-relative input-form">
            <img src="/img/search.svg" class="search position-absolute start-0 top-50 translate-middle-y ms-3" width="20"
              alt="search-icon" />
            <input type="text" class="search form-control ps-40 border-gray-300 fs-14 fw-normal" placeholder="Search"
              v-model="search" @input="handleGlobalSearch" />
          </div>
          <JobAssigneeFilter :users="accountUsers" :jobs="jobsBeforeAssigneeFilter"
            :current-user-id="currentUserId" :value="appliedAssigneeIds" @input="setAssigneeFilter" />
        </div>

        <div class="jobs-view d-flex align-items-center gap-12">
          <div class="d-flex align-items-center gap-12">
            <div class="view-switcher d-flex gap-1 border border-gray-200 rounded p-1">
              <button @click="setView(JOB_VIEW_TYPE.LIST_VIEW)"
                :class="{ 'active': currentView === JOB_VIEW_TYPE.LIST_VIEW }"
                class="view-btn btn py-1 px-2 rounded flex-shrink-0">
                <img src="/img/list.svg" class="d-block" width="14" alt="job-list-view" />
              </button>
              <button @click="setView(JOB_VIEW_TYPE.KANBAN_VIEW)"
                :class="{ 'active': currentView === JOB_VIEW_TYPE.KANBAN_VIEW }"
                class="view-btn btn py-1 px-2 rounded flex-shrink-0">
                <img src="/img/column.svg" class="d-block" width="14" alt="job-column-view" />
              </button>
              <button @click="setView(JOB_VIEW_TYPE.CALENDAR_VIEW)"
                :class="{ 'active': currentView === JOB_VIEW_TYPE.CALENDAR_VIEW }"
                class="view-btn btn py-1 px-2 rounded flex-shrink-0">
                <img src="/img/calendar.svg" class="d-block" width="14" alt="job-calendar-view" />
              </button>
            </div>
            <button class="job-filter-btn" @click="openFilter">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              Filter
              <span v-if="activeFilters.length" class="filter-badge">{{ activeFilters.length }}</span>
            </button>
            <button @click="openJobModal(initialJobStatus())" class="bg-success-500 add-job-btn">
              Add Job
            </button>
            <div class="border-end h-fill border-gray-100"></div>
            <button class="settings-btn" @click="$router.push('/jobs/report')">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
              </svg>
              Report
            </button>
            <button class="settings-btn" @click="openJobStatusPage">
              <img src="/img/cog.svg" width="14" alt="status-page-icon" />
              Settings
            </button>
          </div>
        </div>
      </div>

      <transition name="chip-bar">
        <div v-if="hasActiveFilters" class="chip-bar">
          <div class="chip-scroll">
            <span v-for="f in activeFilters" :key="f.key" class="active-chip">
              <span class="chip-label">{{ f.label }}:</span>
              <span class="chip-value">{{ f.value }}</span>
              <button type="button" class="chip-close" :aria-label="`Remove ${f.label} filter`" @click="removeFilter(f)">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round">
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="6" y1="18" x2="18" y2="6" />
                </svg>
              </button>
            </span>
          </div>
          <button class="chip-clear-all" @click="clearAllApplied">Clear all</button>
        </div>
      </transition>

      <div class="flex-fill overflow-auto content-view" :class="{ 'content-view-with-chips': hasActiveFilters }">
        <!-- Kanban View -->
        <main v-if="currentView === JOB_VIEW_TYPE.LIST_VIEW || currentView === JOB_VIEW_TYPE.KANBAN_VIEW" class="canvas-container position-relative" :class="{ 'd-flex gap-24 px-12 p-12': currentView === JOB_VIEW_TYPE.KANBAN_VIEW }">
          <!-- Dynamic Job Columns based on JobsubStatuses -->
          <JobColumn v-for="status in jobStatuses" :key="status.id" :status="status.id"
            :jobs="getJobsByStatus(status.id)" :is-open="isColumnOpen(status.id)" :status-meta="getStatusMeta(status)"
            :JOB_PRIORITY_META="JOB_PRIORITY_META" :job-tags="jobTags" :job-dates="jobDates" :job-statuses="jobStatuses"
            :account-users="accountUsers" :is-kanban-view="currentView === JOB_VIEW_TYPE.KANBAN_VIEW" :is-list-view="currentView === JOB_VIEW_TYPE.LIST_VIEW" @toggle-collapse="toggleColumnCollapse" @open-job-modal="openJobModal"
            :is-archived-view="appliedFilters.archived === 'archived'"
            @drag-end="onDragEnd" @job-updated="handleJobUpdated" @view-job="viewJob" @duplicate-job="duplicateJob"
            @delete-job="deleteJob" @archive-job="archiveJob" @unarchive-job="unarchiveJob" @edit-job="editJob" />
        </main>

        <!-- Calendar View -->
        <JobCalendar
          v-if="currentView === JOB_VIEW_TYPE.CALENDAR_VIEW"
          :jobs="filteredJobList"
          :job-statuses="jobStatuses"
          :account-users="accountUsers"
          @view-job="viewJob" />
      </div>
      <modalAddJob ref="manageJobModal" :default-job-status="selectedJobStatus" :account-users-prop="accountUsers" :job-tags-prop="jobTags"
        :job-statuses-prop="jobStatuses" @job-created="handleJobCreated" @job-edited="handleJobEdited"></modalAddJob>
      <modalManageTags tag-type="job" />
      <modalDeleteJob ref="deleteJobModal" @confirmed="confirmDeleteJob" />

      <transition name="filter-modal">
        <div v-if="filterModalOpen" class="filter-modal-backdrop" @click.self="closeFilter">
          <div class="filter-modal" role="dialog" aria-labelledby="job-filter-title">
            <div class="filter-modal-header">
              <div>
                <h3 id="job-filter-title" class="filter-modal-title">Filter jobs</h3>
                <p class="filter-modal-sub">Narrow the list by tag, user, priority, status, or date.</p>
              </div>
              <button class="filter-close" @click="closeFilter" aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div class="filter-modal-body">
              <div class="filter-field">
                <label class="filter-label" for="job-filter-tags">Tags</label>
                <Multiselect
                  id="job-filter-tags"
                  v-model="draftFilters.tags"
                  :options="jobTags"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  placeholder="Pick tags"
                  track-by="id"
                  label="name"
                />
              </div>

              <div class="filter-field">
                <label class="filter-label" for="job-filter-user">User</label>
                <Multiselect
                  id="job-filter-user"
                  v-model="draftFilters.assignees"
                  :options="accountUsers"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  placeholder="Any user"
                  track-by="id"
                  label="name"
                />
              </div>

              <div class="filter-field">
                <label class="filter-label" for="job-filter-priority">Priority</label>
                <div class="select-wrap">
                  <select id="job-filter-priority" class="filter-input filter-select" v-model="draftFilters.priority">
                    <option v-for="opt in priorityOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                  </select>
                  <svg class="select-caret" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="1 1 5 5 9 1" />
                  </svg>
                </div>
              </div>

              <div class="filter-field">
                <label class="filter-label" for="job-filter-status">Status</label>
                <div class="select-wrap">
                  <select id="job-filter-status" class="filter-input filter-select" v-model="draftFilters.statusId">
                    <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                  </select>
                  <svg class="select-caret" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="1 1 5 5 9 1" />
                  </svg>
                </div>
              </div>

              <div class="filter-field">
                <label class="filter-label" for="job-filter-archived">Show</label>
                <div class="select-wrap">
                  <select id="job-filter-archived" class="filter-input filter-select" v-model="draftFilters.archived">
                    <option value="active">Active jobs</option>
                    <option value="archived">Archived jobs</option>
                  </select>
                  <svg class="select-caret" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="1 1 5 5 9 1" />
                  </svg>
                </div>
              </div>

              <div class="filter-field">
                <label class="filter-checkbox" for="job-filter-new-activity">
                  <input id="job-filter-new-activity" type="checkbox" v-model="draftFilters.newActivity" />
                  <span>Only jobs with new activity</span>
                </label>
                <p class="filter-help">Comments, decisions, or changes since you last opened the job.</p>
              </div>

              <div class="filter-row two-col">
                <div class="filter-field">
                  <label class="filter-label" for="job-filter-from">Start date</label>
                  <input id="job-filter-from" type="date" class="filter-input" v-model="draftFilters.dateFrom" />
                </div>
                <div class="filter-field">
                  <label class="filter-label" for="job-filter-to">End date</label>
                  <input id="job-filter-to" type="date" class="filter-input" v-model="draftFilters.dateTo" />
                </div>
              </div>
            </div>

            <div class="filter-modal-footer">
              <button class="ghost-btn" :disabled="draftFilterCount === 0" @click="clearDraft">Clear all</button>
              <div class="footer-right">
                <button class="text-btn" @click="closeFilter">Cancel</button>
                <button class="primary-btn" @click="applyFilters">
                  Apply
                  <span v-if="draftFilterCount" class="apply-count">{{ draftFilterCount }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import Datepicker from "vuejs-datepicker";
import Multiselect from "vue-multiselect";

import navigation from "../components/global/navigation.vue";
import modalManageTags from "../components/modals/modal-managetags.vue";
import JobColumn from "../components/job-item/job-column.vue";
import JobCalendar from "../components/job-item/job-calendar.vue";
import JobAssigneeFilter from "../components/job-item/job-assignee-filter.vue";
import modalAddJob from "../components/modals/modal-add-job.vue";
import modalDeleteJob from "../components/modals/modal-delete-job.vue";
import nonPremiumJobScreen from "../components/job-archive/nonPremiumJobScreen.vue";

import EnvService from "../env.settings";
import { ROUTES, JOB_STATUS, JOB_PRIORITY_META, JOB_STATUS_META, MODAL_NAMES, VARIANT, JOB_VIEW_TYPE, JOB_VIEW_TYPE_KEY, JOB_EVENT_TYPE } from "../utils/constants.js";
import { handleDropdownPosition, getJobAssigneeIds } from "../utils/helpers";

const { BILLING, JOB_STATUS: JOB_STATUS_ROUTE } = ROUTES;
const { ADD_JOB_MODAL } = MODAL_NAMES;
const { DANGER } = VARIANT;

function makeEmptyJobFilters() {
  return {
    tags: [],
    assignees: [],
    priority: "",
    statusId: "",
    dateFrom: "",
    dateTo: "",
    archived: "active", // "active" (default) | "archived"
    newActivity: false, // only jobs with activity since the user last opened them
  };
}

const JOB_FILTER_LABEL = {
  assignees: "User",
  priority: "Priority",
  statusId: "Status",
  dateFrom: "From",
  dateTo: "To",
  archived: "Show",
  newActivity: "Activity",
};

function newActivityTotal(job) {
  const a = (job && job.newActivity) || {};
  return (Number(a.comments) || 0) + (Number(a.decisions) || 0) + (Number(a.changes) || 0);
}

function extractJobTagIds(job) {
  if (!job || !Array.isArray(job.jobTagLinks)) return [];
  return job.jobTagLinks.flatMap((link) => {
    if (typeof link.tags === "string") {
      try { return JSON.parse(link.tags); } catch { return []; }
    }
    return Array.isArray(link.tags) ? link.tags : [];
  });
}

export default {
  name: "JobList",
  async mounted() {
    this.isPremiumAccount =
      _.get(this.$A, "Auth.account.subscriptionState.sku", []).includes(
        "enterprise"
      ) ||
      _.get(this.$A, "Auth.account.subscriptionState.sku", []).includes(
        "premium"
      );

    if (!this.isPremiumAccount) {
      this.isLoading = false;
      return;
    }

    try {
      await this.reloadJobs();
    } catch (error) {
      console.error("Failed to load jobs:", error);
      this.$toast("Failed to load jobs", {
        title: "Error",
        variant: DANGER,
      });
    }

    await this.loadJobTags();
    await this.loadJobSubStatuses();
    await this.loadAccountUsers();
    await this.loadAccountDetails();

    this.isLoading = false;
    this.$root.$on('job-tags::reloaded', this.loadJobTags);
    this.connectJobHub();
  },
  beforeDestroy() {
    this.$root.$off('job-tags::reloaded', this.loadJobTags);
    try { this.jobHubConnection && this.jobHubConnection.stop(); } catch (e) { }
  },
  data() {
    return {
      JOB_STATUS,
      JOB_PRIORITY_META,
      JOB_STATUS_META,
      JOB_VIEW_TYPE,
      jobDetailsEnabled: false,
      columnOpenStates: {},
      search: "",
      currentView: JOB_VIEW_TYPE.KANBAN_VIEW,
      jobList: null,
      jobTags: [],
      jobStatuses: [],
      openAssigneeDropdownJobId: null,
      jobDates: {},
      selectedJobStatus: null,
      accountUsers: [],
      accountDetails: null,
      isPremiumAccount: false,
      isLoading: true,
      filterModalOpen: false,
      draftFilters: makeEmptyJobFilters(),
      appliedFilters: makeEmptyJobFilters(),
      jobHubConnection: null,
    };
  },
  components: {
    navigation,
    Datepicker,
    Multiselect,
    modalManageTags,
    modalAddJob,
    modalDeleteJob,
    JobColumn,
    JobCalendar,
    JobAssigneeFilter,
    nonPremiumJobScreen,
  },
  computed: {
    dynamicJobStatusMeta() {
      const meta = {};
      this.jobStatuses.forEach((Status) => {
        meta[Status.id] = {
          label: Status.name,
          icon: Status.icon || JOB_STATUS_META[Status.statusCategoryID].icon,
        };
      });
      return meta;
    },
    priorityOptions() {
      return [
        { value: "", label: "Any priority" },
        ...Object.entries(JOB_PRIORITY_META).map(([k, v]) => ({ value: k, label: v.label })),
      ];
    },
    currentUserId() {
      return this.$A.Auth.id;
    },
    appliedAssigneeIds() {
      return this.appliedFilters.assignees.map((u) => u.id);
    },
    jobsBeforeAssigneeFilter() {
      return this.filterJobs({ ...this.appliedFilters, assignees: [] });
    },
    statusOptions() {
      return [
        { value: "", label: "Any status" },
        ...(this.jobStatuses || []).map((s) => ({ value: String(s.id), label: s.name })),
      ];
    },
    draftFilterCount() {
      const f = this.draftFilters;
      let n = 0;
      if (f.tags.length) n++;
      if (f.assignees.length) n++;
      if (f.priority !== "" && f.priority != null) n++;
      if (f.statusId) n++;
      if (f.dateFrom) n++;
      if (f.dateTo) n++;
      if (f.archived && f.archived !== "active") n++;
      if (f.newActivity) n++;
      return n;
    },
    activeFilters() {
      const items = [];
      const a = this.appliedFilters;
      for (const t of a.tags) {
        items.push({ key: `tag:${t.id}`, label: "Tag", value: t.name, removeTagId: t.id });
      }
      for (const u of a.assignees) {
        items.push({ key: `assignee:${u.id}`, label: JOB_FILTER_LABEL.assignees, value: u.name || u.email, removeAssigneeId: u.id });
      }
      if (a.priority !== "" && a.priority != null) {
        const p = JOB_PRIORITY_META[a.priority];
        items.push({ key: "priority", label: JOB_FILTER_LABEL.priority, value: p ? p.label : a.priority });
      }
      if (a.statusId) {
        const s = (this.jobStatuses || []).find((x) => String(x.id) === String(a.statusId));
        items.push({ key: "statusId", label: JOB_FILTER_LABEL.statusId, value: s ? s.name : a.statusId });
      }
      if (a.dateFrom) items.push({ key: "dateFrom", label: JOB_FILTER_LABEL.dateFrom, value: a.dateFrom });
      if (a.dateTo) items.push({ key: "dateTo", label: JOB_FILTER_LABEL.dateTo, value: a.dateTo });
      if (a.archived && a.archived !== "active") {
        items.push({ key: "archived", label: JOB_FILTER_LABEL.archived, value: "Archived" });
      }
      if (a.newActivity) {
        items.push({ key: "newActivity", label: JOB_FILTER_LABEL.newActivity, value: "New since you looked" });
      }
      return items;
    },
    hasActiveFilters() {
      return this.activeFilters.length > 0;
    },
    filteredJobList() {
      return this.filterJobs(this.appliedFilters);
    },
  },
  methods: {
    filterJobs(a) {
      const list = this.jobList || [];
      const tagIdSet = a.tags.length ? new Set(a.tags.map((t) => t.id)) : null;
      const assigneeIds = a.assignees.length ? a.assignees.map((u) => u.id) : null;
      const priority = a.priority !== "" && a.priority != null ? Number(a.priority) : null;
      const statusId = a.statusId ? Number(a.statusId) : null;
      const fromTs = a.dateFrom ? new Date(a.dateFrom).getTime() : null;
      const toTs = a.dateTo ? new Date(a.dateTo).getTime() + 24 * 60 * 60 * 1000 - 1 : null;
      if (!tagIdSet && assigneeIds == null && priority == null && statusId == null && fromTs == null && toTs == null && !a.newActivity) {
        return list;
      }
      return list.filter((job) => {
        if (a.newActivity && newActivityTotal(job) === 0) return false;
        if (assigneeIds != null && !getJobAssigneeIds(job).some((id) => assigneeIds.includes(id))) return false;
        if (priority != null && Number(job.priority) !== priority) return false;
        if (statusId != null && job.status !== statusId) return false;
        if (tagIdSet) {
          const jobTagIds = extractJobTagIds(job);
          if (!jobTagIds.some((id) => tagIdSet.has(id))) return false;
        }
        if (fromTs != null) {
          const sd = job.startDate ? new Date(job.startDate).getTime() : null;
          if (sd == null || sd < fromTs) return false;
        }
        if (toTs != null) {
          const ed = job.endDate ? new Date(job.endDate).getTime() : null;
          if (ed == null || ed > toTs) return false;
        }
        return true;
      });
    },
    async loadAccountDetails() {
      try {
        this.accountDetails = await this.$A.AccountService.AccountStats();
      } catch (error) {
        console.error("Failed to load account:", error);
      }
    },

    async loadAccountUsers() {
      try {
        this.accountUsers = await this.$A.AccountService.All();
      } catch (error) {
        console.error("Failed to load account users:", error);
        this.accountUsers = [];
      }
    },

    async duplicateJob(job) {
      try {
        await this.$A.JobService.duplicateJob(job.id);
        await this.handleJobUpdated();
      } catch (e) {
        console.error("Failed to duplicate job", e);
        this.$toast("Failed to duplicate job", {
          title: "Error",
          variant: DANGER,
        });
      }
    },

    async onDragEnd(evt) {
      const { from, to, newIndex, oldIndex } = evt;

      if (!from || !to) return;
      
      if (from === to && newIndex === oldIndex) return;

      // Get the job data from the drag event
      let job = null;

      // Method 1: Try to get job from the dragged element's data
      if (evt.item && evt.item.dataset && evt.item.dataset.jobId) {
        job = this.findJobById(parseInt(evt.item.dataset.jobId));
      }

      // Method 2: Try to get job from the old index in the source column
      if (!job && oldIndex !== undefined && from.dataset.status) {
        const sourceStatus = parseInt(from.dataset.status);
        const sourceJobs = this.getJobsByStatus(sourceStatus);
        if (sourceJobs && sourceJobs[oldIndex]) {
          job = sourceJobs[oldIndex];
        }
      }

      // Method 3: Try to get job from the new index in the target column
      if (!job && newIndex !== undefined && to.dataset.status) {
        const targetStatus = parseInt(to.dataset.status);
        const targetJobs = this.getJobsByStatus(targetStatus);
        if (targetJobs && targetJobs[newIndex]) {
          job = targetJobs[newIndex];
        }
      }

      // Method 4: Fallback - find job by comparing job lists before and after
      if (!job) {
        // This is a more complex fallback - you might need to implement this
        console.warn(
          "Could not determine which job was moved, using fallback method"
        );
        return;
      }

      if (!job) {
        console.error("Job not found in drag event");
        return;
      }

      const newStatus = parseInt(to.dataset.status);
      const oldStatus = job.status;

      try {
        const targetJobs = this.getJobsByStatus(newStatus);
        
        // For same-column drags, we need to exclude the job being moved from the target array
        const jobsForPositioning = newStatus === oldStatus 
          ? targetJobs.filter(j => j.id !== job.id)
          : targetJobs;

        let InsertAfterRank = null;
        let InsertBeforeRank = null;

        switch (true) {
          case jobsForPositioning.length === 0:
            InsertAfterRank = null;
            InsertBeforeRank = null;
            break;
          case newIndex === 0:
            InsertBeforeRank = jobsForPositioning[0].position;
            InsertAfterRank = null;
            break;
          case newIndex >= jobsForPositioning.length:
            InsertAfterRank = jobsForPositioning[jobsForPositioning.length - 1].position;
            InsertBeforeRank = null;
            break;
          default:
            InsertBeforeRank = jobsForPositioning[newIndex].position;
            InsertAfterRank = jobsForPositioning[newIndex - 1].position;
            break;
        }

        await this.$A.JobService.moveJob(job.id, newStatus, InsertAfterRank, InsertBeforeRank);

        if (newStatus !== oldStatus) {
          job.status = newStatus;
        }

        if (newStatus !== oldStatus) {
          const newStatusMeta = this.dynamicJobStatusMeta[newStatus];
          const statusLabel = newStatusMeta
            ? newStatusMeta.label
            : "Unknown Status";

          this.$toast(`Job moved to ${statusLabel}`, {
            title: "Success",
            variant: "success",
          });
        } else {
          this.$toast(`Job position updated`, {
            title: "Success",
            variant: "success",
          });
        }

        await this.reloadJobs();
      } catch (error) {
        console.error("Failed to move job:", error);

        await this.reloadJobs();

        this.$toast("Failed to move job", {
          title: "Error",
          variant: DANGER,
        });
      }
      finally {
        // Recalculate dropdown positions even on error
        this.$nextTick(() => {
          handleDropdownPosition();
        });
      }
    },

    // Live "new activity" counters: every job event for the account is broadcast on the job hub.
    connectJobHub() {
      try {
        if (this.jobHubConnection) return;
        this.jobHubConnection = this.$A.RealtimeService.Connect('job_hub');
        this.jobHubConnection.on('ReadyForRegistration', async () => {
          try {
            const token = _.get(this, '$A.Auth.apiToken.token', '');
            if (token) await this.jobHubConnection.invoke('RegisterJobConnection', token, 'main');
          } catch (e) {
            console.error('Job hub registration error:', e);
          }
        });
        this.jobHubConnection.on('ReceiveJobEvents', this.applyLiveJobEvent);
      } catch (e) {
        console.error('Error connecting to job hub:', e);
      }
    },
    applyLiveJobEvent(payload) {
      if (!payload || !payload.jobId || payload.action || !payload.eventTypeId) return;
      if (Number(payload.userId) === Number(this.currentUserId)) return;
      const job = (this.jobList || []).find((j) => Number(j.id) === Number(payload.jobId));
      if (!job) return;
      const current = job.newActivity || { comments: 0, decisions: 0, changes: 0 };
      const isComment = Number(payload.eventTypeId) === JOB_EVENT_TYPE.Commented;
      this.$set(job, 'newActivity', {
        ...current,
        comments: (Number(current.comments) || 0) + (isComment ? 1 : 0),
        changes: (Number(current.changes) || 0) + (isComment ? 0 : 1),
      });
    },

    async reloadJobs() {
      this.jobList = await this.$A.JobService.listJobs({
        query: this.search ? this.search.trim() : null,
        archived: this.appliedFilters.archived === "archived",
      });
    },

    async handleJobUpdated() {
      try {
        await this.reloadJobs();
      } catch (error) {
        console.error("Failed to refresh job list:", error);
      }
    },

    async loadJobTags() {
      try {
        this.jobTags = await this.$A.JobTagService.GetAll();
      } catch (error) {
        console.error("Failed to load job tags:", error);
        this.jobTags = [];
      }
    },

    async loadJobSubStatuses() {
      try {
        this.jobStatuses = await this.$A.JobStatusService.getAllJobStatuses();
        this.jobStatuses.sort((a, b) => {
          if (a.statusCategoryID !== b.statusCategoryID) {
            return a.statusCategoryID - b.statusCategoryID;
          }
          return new Date(a.createdAt) - new Date(b.createdAt);
        });

        this.jobStatuses.forEach((status) => {
          this.$set(this.columnOpenStates, status.id, true);
        });
      } catch (error) {
        console.error("Failed to load job sub statuses:", error);
        this.jobStatuses = [];
      }
    },

    async uploadPendingJobFiles(jobId, pendingFiles) {
      const token = this.$A.Auth.apiToken.token;
      const base = EnvService.instance.apiHost.replace(/\/+$/, "");
      const url = `${base}/jobs/${jobId}/files?access_token=${encodeURIComponent(token)}`;
      const failed = [];
      await Promise.all(
        pendingFiles.map(async (p) => {
          try {
            const form = new FormData();
            form.append("file", p.file, p.name);
            const res = await fetch(url, { method: "POST", body: form });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
          } catch (e) {
            console.error("Pending job file upload failed:", e);
            failed.push(p.name);
          }
        })
      );
      if (failed.length) {
        this.$toast(`Failed to upload: ${failed.join(", ")}`, {
          title: "Upload error",
          variant: DANGER,
        });
      }
    },

    async handleJobCreated(newJob) {
      try {
        const createJobRequest = {
          name: newJob.name || null,
          description: newJob.description || null,
          assignees: Array.isArray(newJob.assignees) ? newJob.assignees : [],
          status: newJob.status || null,
          priority: newJob.priority || null,
          startDate: newJob.startDate || null,
          endDate: newJob.endDate || null,
          tags: {
            update: Array.isArray(newJob.tags) ? newJob.tags : [],
            delete: [],
          }
        };
        const createdJob = await this.$A.JobService.createJob(createJobRequest);

        if (createdJob && createdJob.jobId && Array.isArray(newJob._pendingFiles) && newJob._pendingFiles.length) {
          await this.uploadPendingJobFiles(createdJob.jobId, newJob._pendingFiles);
        }

        if (createdJob && createdJob.jobId && Array.isArray(newJob._pendingContactIds) && newJob._pendingContactIds.length) {
          try {
            await this.$A.JobService.updateContactLink({
              jobId: createdJob.jobId,
              update: newJob._pendingContactIds,
              delete: [],
            });
          } catch (e) {
            console.error("Failed to attach contacts to new job:", e);
          }
        }

        await this.handleJobUpdated();

        // Navigate to job details page after successful creation
        if (createdJob && createdJob.jobId) {
          window.location.href = `/job-details/${createdJob.jobId}`;
        }
      } catch (error) {
        console.error("Error handling job creation:", error);
        this.$toast("Failed to create job", {
          title: "Error",
          variant: DANGER,
        });
      }
    },

    async handleJobEdited({ jobId, jobData }) {
      try {
        const contactsToAdd = Array.isArray(jobData._contactsToAdd) ? jobData._contactsToAdd : [];
        const contactsToRemove = Array.isArray(jobData._contactsToRemove) ? jobData._contactsToRemove : [];
        const { _contactsToAdd, _contactsToRemove, ...persistableJobData } = jobData;

        const updateJobRequest = {
          jobId: jobId,
          jobData: persistableJobData
        };

        await this.$A.JobService.updateJobById(updateJobRequest);

        if (contactsToAdd.length || contactsToRemove.length) {
          try {
            await this.$A.JobService.updateContactLink({
              jobId,
              update: contactsToAdd,
              delete: contactsToRemove,
            });
          } catch (e) {
            console.error("Failed to update job contacts:", e);
          }
        }

        await this.handleJobUpdated();

        this.$toast("Job updated successfully", {
          title: "Success",
          variant: "success",
        });
      } catch (error) {
        console.error("Error updating job:", error);
        this.$toast("Failed to update job", {
          title: "Error",
          variant: DANGER,
        });
      }
    },

    getJobsByStatus(subStatusId) {
      return this.filteredJobList
        .filter(job => job && job.status === subStatusId);
    },

    openFilter() {
      this.draftFilters = {
        ...this.appliedFilters,
        tags: [...this.appliedFilters.tags],
        assignees: [...this.appliedFilters.assignees],
      };
      this.filterModalOpen = true;
    },
    closeFilter() {
      this.filterModalOpen = false;
    },
    applyFilters() {
      const scopeChanged = this.appliedFilters.archived !== this.draftFilters.archived;
      this.appliedFilters = {
        ...this.draftFilters,
        tags: [...this.draftFilters.tags],
        assignees: [...this.draftFilters.assignees],
      };
      this.filterModalOpen = false;
      // The archived scope is server-side, so re-fetch when it changes.
      if (scopeChanged) this.reloadJobs();
    },
    clearDraft() {
      this.draftFilters = makeEmptyJobFilters();
    },
    clearAllApplied() {
      const scopeChanged = this.appliedFilters.archived !== "active";
      this.appliedFilters = makeEmptyJobFilters();
      if (scopeChanged) this.reloadJobs();
    },
    setAssigneeFilter(userIds) {
      this.appliedFilters = {
        ...this.appliedFilters,
        assignees: (this.accountUsers || []).filter((u) => userIds.includes(u.id)),
      };
    },
    removeFilter(chip) {
      if (chip.removeTagId != null) {
        this.appliedFilters = {
          ...this.appliedFilters,
          tags: this.appliedFilters.tags.filter((t) => t.id !== chip.removeTagId),
        };
        return;
      }
      if (chip.removeAssigneeId != null) {
        this.appliedFilters = {
          ...this.appliedFilters,
          assignees: this.appliedFilters.assignees.filter((u) => u.id !== chip.removeAssigneeId),
        };
        return;
      }
      if (chip.key === "archived") {
        this.appliedFilters = { ...this.appliedFilters, archived: "active" };
        this.reloadJobs();
        return;
      }
      if (chip.key === "newActivity") {
        this.appliedFilters = { ...this.appliedFilters, newActivity: false };
        return;
      }
      this.appliedFilters = { ...this.appliedFilters, [chip.key]: "" };
    },

    isColumnOpen(subStatusId) {
      return this.columnOpenStates[subStatusId] !== false;
    },

    getStatusMeta(status) {
      return {
        title: status.name,
        icon: status.icon || JOB_STATUS_META[status.statusCategoryID].icon,
      };
    },

    findJobById(jobId) {
      return this.jobList?.find((job) => job.id === jobId);
    },

    openBillingPage() {
      this.$router.push(BILLING);
    },

    openJobStatusPage() {
      this.$router.push(JOB_STATUS_ROUTE);
    },

    openJobModal(status) {
      this.selectedJobStatus = status || this.jobStatuses[0]?.id;
      this.$bvModal.show(ADD_JOB_MODAL);
    },

    formatDate(date) {
      const dateObj = new Date(date);
      const options = { month: "short", day: "2-digit", year: "numeric" };
      return dateObj.toLocaleDateString("en-US", options);
    },

    toggleDatePicker(refName) {
      const datePicker = this.$refs[refName];
      if (!datePicker) return;
      const calendarEl = datePicker[0].$children[1].$el;
      if (calendarEl) {
        calendarEl.style.display =
          calendarEl.style.display === "none" || !calendarEl.style.display
            ? "block"
            : "none";
      }
    },

    toggleAssigneeDropdown(jobId) {
      this.openAssigneeDropdownJobId =
        this.openAssigneeDropdownJobId === jobId ? null : jobId;
    },

    closeAssigneeDropdown() {
      this.openAssigneeDropdownJobId = null;
    },

    viewJob(job) {
      window.location.href = `/job-details/${job.id}`;
    },

    editJob(job) {
      // Check if the modal has an openWithJob method for editing
      if (this.$refs.manageJobModal && typeof this.$refs.manageJobModal.openWithJob === 'function') {
        this.$refs.manageJobModal.openWithJob(job);
      } else {
        // Fallback: navigate to job details page
        window.location.href = `/job-details/${job.id}`;
      }
    },

    handleGlobalSearch: _.debounce(async function () {
      try {
        await this.reloadJobs();
      } catch (e) {
        console.error("Global job search failed:", e);
      }
    }, 300),

    toggleColumnCollapse(subStatusId) {
      this.$set(
        this.columnOpenStates,
        subStatusId,
        !this.columnOpenStates[subStatusId]
      );
    },

    async archiveJob(job) {
      try {
        await this.$A.JobService.archiveJob(job.id);
        await this.handleJobUpdated();
      } catch (e) {
        console.error("Failed to archive job", e);
        this.$toast("Failed to archive job", {
          title: "Error",
          variant: DANGER,
        });
      }
    },

    async unarchiveJob(job) {
      try {
        await this.$A.JobService.unarchiveJob(job.id);
        await this.handleJobUpdated();
      } catch (e) {
        console.error("Failed to restore job", e);
        this.$toast("Failed to restore job", {
          title: "Error",
          variant: DANGER,
        });
      }
    },

    deleteJob(job) {
      this.$refs.deleteJobModal.open(job);
    },

    async confirmDeleteJob(job) {
      try {
        await this.$A.JobService.deleteJob(job.id);
        await this.handleJobUpdated();
      } catch (e) {
        console.error("Failed to delete job", e);
        this.$toast("Failed to delete job", {
          title: "Error",
          variant: DANGER,
        });
      }
    },
    initialJobStatus() {
      return this.jobStatuses.find(status => status.statusCategoryID === 2)?.id || this.jobStatuses[0]?.id
    },
    setView(viewType) {
      this.currentView = viewType;
    }
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.nav-margin {
  min-height: 100vh;
}

.content-view {
  height: calc(100vh - 130px);
}

.search {
  max-width: 320px;
  border-radius: 6px;
}

.add-job-btn {
  border-radius: 6px;
  border: none;
}

.settings-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease;
}
.settings-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}
.settings-btn img {
  opacity: 0.7;
}

/* Filter button */
.job-filter-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  background: transparent;
  border: 1px solid transparent;
  color: #4b5563;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 120ms ease, color 120ms ease;
}
.job-filter-btn:hover {
  background: #f3f4f6;
  color: #111827;
}
.filter-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #019256;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.01em;
  box-shadow: 0 0 0 2px #fff;
}

/* Chip bar */
.chip-bar {
  position: sticky;
  top: 56px;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: #fbfbfc;
  border-bottom: 1px solid #eceef1;
  min-height: 48px;
}
.chip-scroll {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.active-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 6px 0 10px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #e1e4ea;
  font-size: 12px;
  color: #1f2937;
  box-shadow: 0 1px 1px rgba(15, 23, 42, 0.03);
}
.active-chip:hover {
  border-color: rgba(1, 146, 86, 0.45);
  box-shadow: 0 1px 2px rgba(1, 146, 86, 0.08);
}
.chip-label {
  color: #6b7280;
  font-weight: 500;
}
.chip-value {
  color: #111827;
  font-weight: 600;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chip-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-left: 2px;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 999px;
  color: #9ca3af;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease, transform 120ms ease;
}
.chip-close:hover {
  background: rgba(1, 146, 86, 0.12);
  color: #019256;
  transform: scale(1.06);
}
.chip-clear-all {
  flex-shrink: 0;
  background: transparent;
  border: 0;
  color: #6b7280;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
}
.chip-clear-all:hover {
  color: #019256;
  background: rgba(1, 146, 86, 0.08);
}
.chip-bar-enter-active,
.chip-bar-leave-active {
  transition: opacity 140ms ease, transform 160ms ease;
}
.chip-bar-enter-from,
.chip-bar-enter,
.chip-bar-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.content-view-with-chips {
  height: calc(100vh - 178px) !important;
}

/* Filter modal */
.filter-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 80px;
  z-index: 1080;
}
.filter-modal {
  background: #fff;
  width: 100%;
  max-width: 560px;
  border-radius: 12px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.28);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.filter-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px 14px;
  border-bottom: 1px solid #f1f3f5;
}
.filter-modal-title {
  font-family: 'Open Sans', system-ui, -apple-system, sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px;
}
.filter-modal-sub {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}
.filter-close {
  background: transparent;
  border: 0;
  color: #6b7280;
  padding: 4px;
  border-radius: 6px;
  cursor: pointer;
}
.filter-close:hover {
  background: #f3f4f6;
  color: #111827;
}
.filter-modal-body {
  padding: 18px 24px 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 60vh;
  overflow-y: auto;
}
.filter-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.filter-checkbox {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
}
.filter-checkbox input {
  width: 16px;
  height: 16px;
  accent-color: #019256;
}
.filter-help {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
}
.filter-row.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.filter-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  letter-spacing: 0.01em;
  text-transform: uppercase;
}
.filter-input {
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #1f2937;
  background: #fff;
  outline: none;
  transition: border-color 120ms ease, box-shadow 120ms ease;
  font-family: inherit;
}
.filter-input:focus {
  border-color: #019256;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}
.select-wrap {
  position: relative;
}
.filter-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding-right: 32px;
  cursor: pointer;
}
.select-caret {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}
.filter-field >>> .multiselect {
  min-height: 38px;
  font-size: 14px;
}
.filter-field >>> .multiselect__tags {
  min-height: 38px;
  padding: 6px 36px 0 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
}
.filter-field >>> .multiselect--active .multiselect__tags {
  border-color: #019256;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}
.filter-field >>> .multiselect__tag {
  background: #019256;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  border-radius: 6px;
  padding: 3px 24px 3px 8px;
}
.filter-field >>> .multiselect__option--highlight {
  background: rgba(1, 146, 86, 0.12);
  color: #111827;
}
.filter-field >>> .multiselect__option--selected.multiselect__option--highlight {
  background: #019256;
  color: #fff;
}
.filter-modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 24px 18px;
  border-top: 1px solid #f1f3f5;
  background: #fbfbfc;
}
.footer-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.primary-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #019256;
  color: #fff;
  border: 0;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(1, 146, 86, 0.2);
  transition: background-color 120ms ease;
}
.primary-btn:hover {
  background: #017a48;
}
.apply-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.2);
  font-size: 11px;
  font-weight: 700;
}
.ghost-btn {
  background: #fff;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 9px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
}
.ghost-btn:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #9ca3af;
}
.ghost-btn:disabled {
  color: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
}
.text-btn {
  background: transparent;
  border: 0;
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
}
.text-btn:hover {
  color: #111827;
  background: #f3f4f6;
}
.filter-modal-enter-active,
.filter-modal-leave-active {
  transition: opacity 160ms ease;
}
.filter-modal-enter-from,
.filter-modal-enter,
.filter-modal-leave-to {
  opacity: 0;
}

.py-14 {
  padding-top: 14px !important;
  padding-bottom: 14px !important;
}

.border-gray-200 {
  border-color: #dee2e6 !important;
}

.border-gray-300 {
  border-color: #ced4da !important;
}

.text-gray-900 {
  color: #212529 !important;
}

.text-gray-700 {
  color: #495057 !important;
}

.text-gray-500 {
  color: #adb5bd !important;
}

.bg-gray-50 {
  background-color: #f8f9fa !important;
}

.box-32 {
  width: 32px;
  height: 32px;
}

.lh-1 {
  line-height: 1 !important;
}

.rounded-3,
.rounded-3 * {
  border-radius: 0.5rem !important;
}

.collapse-toogle {
  outline: none;
}

.flex-shrink-0 {
  flex-shrink: 0 !important;
}

.ps-2 {
  padding-left: 0.5rem !important;
}

.ps-3 {
  padding-left: 1rem !important;
}

.ps-12 {
  padding-left: 3rem !important;
}

.ps-40 {
  padding-left: 2.5rem !important;
}

.ms-3 {
  margin-left: 1rem !important;
}

.ms-12 {
  margin-left: 3rem !important;
}

.start-0 {
  left: 0 !important;
}

.search.top-50 {
  top: 50% !important;
}

.translate-middle-y {
  transform: translateY(-50%) !important;
}

.font-weight-semibold {
  font-weight: 600 !important;
}

.dot {
  width: 8px;
  height: 8px;
  background: #adb5bd;
  border-radius: 50%;
  display: inline-block;
  margin-right: 8px;
}

.upgrade-btn {
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  padding: 9px 13px;
  background-color: #eef2f5;
}

.bg-success-500 {
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  padding: 9px 13px;
  color: #f9f9f9;
}

.h-fill {
  height: 45px !important;
}

.action-bar {
  position: sticky !important;
  z-index: 100 !important;
}

@media (min-width: 992px) {
  .action-bar {
    top: 64px;
  }
}

@media (max-width: 992px) {
  .action-bar {
    align-items: stretch;
    gap: 1rem;
  }

  .account-nav {
    justify-content: center;
    background-color: #ffffff;
  }
}

@media (max-width: 480px) {
  .action-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .jobs-view {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .input-form {
    max-width: 220px;
    min-width: 220px;
  }
}

.input-form {
  min-width: 320px;
  max-width: 320px;
}

.view-switcher {
  background-color: #f8f9fa;
}

.view-btn {
  background-color: transparent;
  border: none;
  transition: background-color 0.2s;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px !important;
}

.view-btn:hover {
  background-color: #e9ecef;
}

.view-btn.active {
  background-color: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.view-btn img {
  opacity: 0.6;
}

.view-btn.active img {
  opacity: 1;
}

@media(max-width: 592px) {
  .content-view{
    min-height: calc(100vh - 177px);
  }
}
</style>
